import { ChatCustomFilter, ChatDateFilter, ChatFilterOperator } from '@trinity/chat';
import {
  InteractionTypeEnum,
} from '@trinity/chat';
import { InteractionTypesMap } from '@trinity/chat';
import type { CustomFilter } from '@trinity/chat';
import { toTitleCase } from '@trinity/chat-components/utils';

export function getInteractionTypes() {
  return [
    InteractionTypeEnum.CallCenterInteraction,
    InteractionTypeEnum.FbmsInteraction,
    InteractionTypeEnum.DocumentedInterest,
    InteractionTypeEnum.MedicalInsight,
  ].map(m => ({ id: m, name: InteractionTypesMap[m] }));
}

export function getPracticeSettings() {
  return [
    {
      id: '1',
      name: 'Option 1',
    },
    {
      id: '2',
      name: 'Option 2',
    },
  ];
}

enum Sentiment {
  Positive = 'positive',
  Negative = 'negative',
  Neutral = 'neutral',
}

export function getSentimentValues() {
  return [
    Sentiment.Positive,
    Sentiment.Neutral,
    Sentiment.Negative,
  ].map(m => ({ id: m, name: toTitleCase(m) }));
}

export function getCustomFilterOperators(type: ChatCustomFilter) {
  const operatorsMap: Record<ChatCustomFilter, ChatFilterOperator[]> = {
    [ChatCustomFilter.CallCenterSource]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.CallCenterSubSource]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.InteractionDate]: [ChatFilterOperator.MoreRecentThan, ChatFilterOperator.PriorTo],
    [ChatCustomFilter.PracticeSetting]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.InsightType]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.Product]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.Source]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.TherapeuticArea]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.TherapeuticSubArea]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.Sentiment]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.InterestArea]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],

    [ChatCustomFilter.InteractionType]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.InteractionSubType]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.InteractionDetail]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.AgentFilter]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],

    [ChatCustomFilter.InteractionStartEndDate]: [ChatFilterOperator.MoreRecentThan, ChatFilterOperator.PriorTo],
  };

  return operatorsMap[type] || [];
}

export function getDefaultOperator(type: ChatCustomFilter) {
  return getCustomFilterOperators(type)[0];
}

export function getDefaultCustomFilterValue<T extends ChatCustomFilter>(type: T): CustomFilter.Item {
  switch (type) {
    case ChatCustomFilter.InteractionDate:
      return {
        type: ChatCustomFilter.InteractionDate,
        operator: ChatFilterOperator.MoreRecentThan,
        value: ChatDateFilter.Last30Days,
      } as CustomFilter.Item;

    case ChatCustomFilter.PracticeSetting:
    case ChatCustomFilter.InsightType:
    case ChatCustomFilter.Product:
    case ChatCustomFilter.Source:
    case ChatCustomFilter.TherapeuticArea:
    case ChatCustomFilter.TherapeuticSubArea:
    case ChatCustomFilter.Sentiment:
    case ChatCustomFilter.InterestArea:
    case ChatCustomFilter.InteractionType:
    case ChatCustomFilter.InteractionSubType:
    case ChatCustomFilter.InteractionDetail:
    case ChatCustomFilter.CallCenterSource:
    case ChatCustomFilter.CallCenterSubSource:
    case ChatCustomFilter.AgentFilter:
      return {
        operator: ChatFilterOperator.Eq,
        value: [],
        type,
      } as CustomFilter.Item;

    case ChatCustomFilter.InteractionStartEndDate:
      return {
        type: ChatCustomFilter.InteractionStartEndDate,
        operator: ChatFilterOperator.MoreRecentThan,
        value: null, // TODO: implement
      } as CustomFilter.Item;

    default:
      throw new UnreachableCaseError(type);
  }
}

export function validateCustomFilterValue<T extends ChatCustomFilter>(value: CustomFilter.Item<T>) {
  switch (value.type) {
    case ChatCustomFilter.InteractionDate:
      return !!value.value;

    case ChatCustomFilter.PracticeSetting:
    case ChatCustomFilter.InsightType:
    case ChatCustomFilter.Product:
    case ChatCustomFilter.Source:
    case ChatCustomFilter.TherapeuticArea:
    case ChatCustomFilter.TherapeuticSubArea:
    case ChatCustomFilter.Sentiment:
    case ChatCustomFilter.InterestArea:
    case ChatCustomFilter.InteractionType:
    case ChatCustomFilter.InteractionSubType:
    case ChatCustomFilter.InteractionDetail:
    case ChatCustomFilter.CallCenterSource:
    case ChatCustomFilter.CallCenterSubSource:
    case ChatCustomFilter.AgentFilter:
      return !!value.value.length;

    case ChatCustomFilter.InteractionStartEndDate:
      return false; // TODO: implement

    default:
      throw new UnreachableCaseError(value);
  }
}